// pages/features.js

import React from "react";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";

import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ScoreboardRoundedIcon from "@mui/icons-material/ScoreboardRounded";
import SportsHockeyRoundedIcon from "@mui/icons-material/SportsHockeyRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TvRoundedIcon from "@mui/icons-material/TvRounded";

import LandingLayout from "../components/layout/LandingLayout";

const sectionNavigation = [
  {
    id: "timekeeping",
    label: "Хронометраж",
  },
  {
    id: "match-events",
    label: "Счёт и удаления",
  },
  {
    id: "scoreboard",
    label: "Хоккейное табло",
  },
  {
    id: "teams",
    label: "Команды и составы",
  },
  {
    id: "remote-control",
    label: "Управление с телефона",
  },
  {
    id: "media-center",
    label: "Медиацентр",
  },
  {
    id: "matches-history",
    label: "Матчи и турниры",
  },
];

const featureSections = [
  {
    id: "timekeeping",
    eyebrow: "Игровое время",
    title: "Хоккейный таймер и управление игровым временем",
    icon: AccessTimeRoundedIcon,
    image: "/features/match.png",
    alt: "Хоккейный таймер и панель управления игровым временем СпортПодсчет Хоккей",
    reverse: false,
    accent: "#0073FF",
    paragraphs: [
      "СпортПодсчет Хоккей позволяет оператору управлять игровым временем непосредственно во время матча. Все основные действия находятся в одном рабочем окне и доступны без перехода между несколькими программами.",
      "Таймер можно запускать, останавливать и корректировать вручную. Система учитывает периоды, перерывы и дополнительные стадии хоккейного матча.",
    ],
    features: [
      "Запуск и остановка игрового времени",
      "Ручная установка минут и секунд",
      "Быстрое прибавление и уменьшение времени",
      "Предматчевый режим и начало первого периода",
      "Переходы между периодами и перерывами",
      "Овертайм и серия послематчевых буллитов",
      "Отображение десятых долей секунды",
      "Сохранение текущего состояния таймера",
    ],
    seoText:
      "Программа подходит для использования как хоккейный таймер на компьютере оператора, система хронометража для ледовой арены или рабочее место судьи времени.",
  },
  {
    id: "match-events",
    eyebrow: "Ход матча",
    title: "Счёт, удаления и события хоккейного матча",
    icon: SportsHockeyRoundedIcon,
    image: "/screens/control_panel.webp",
    alt: "Управление счётом, удалениями и событиями хоккейного матча",
    reverse: true,
    accent: "#0A8FDE",
    paragraphs: [
      "Оператор может управлять счётом и основными событиями матча из той же панели, в которой работает игровой таймер. Гол можно добавить каждой команде или отменить, если действие было выполнено ошибочно.",
      "Для удалений используется отдельный учёт штрафного времени. Информация отображается оператору и может одновременно выводиться на зрительское табло.",
    ],
    features: [
      "Добавление гола каждой команде",
      "Отмена ошибочно добавленного гола",
      "Выбор команды и удалённого игрока",
      "Указание номера и фамилии хоккеиста",
      "Несколько одновременных удалений",
      "Отдельный таймер каждого удаления",
      "Продолжение штрафного времени в следующем периоде",
      "Тайм-ауты команд и другие события матча",
    ],
    seoText:
      "СпортПодсчет Хоккей можно использовать как программу учёта удалений, таймер штрафного времени и систему управления счётом хоккейного матча.",
  },
  {
    id: "scoreboard",
    eyebrow: "Экран для зрителей",
    title:
      "Хоккейное табло на монитор, телевизор, проектор или LED-экран",
    icon: ScoreboardRoundedIcon,
    image: "/features/scoreboard.png",
    alt: "Зрительское хоккейное табло СпортПодсчет Хоккей на отдельном экране",
    reverse: false,
    accent: "#00A76F",
    paragraphs: [
      "Зрительское табло открывается отдельно от панели оператора. Его можно вывести на второй монитор, телевизор, проектор или светодиодный экран, который принимает видеосигнал с компьютера.",
      "Табло показывает основную информацию о ходе игры крупно и понятно для зрителей. Внешний вид можно адаптировать под разрешение экрана и оформление конкретной площадки.",
    ],
    features: [
      "Счёт обеих команд",
      "Игровое время и текущий период",
      "Названия и логотипы команд",
      "Отображение текущих удалений",
      "Название матча или турнира",
      "Полноэкранный режим",
      "Выбор монитора для открытия табло",
      "Работа с телевизором, проектором и LED-экраном",
    ],
    secondaryTitle: "Настройка дизайна хоккейного табло",
    secondaryFeatures: [
      "Изменение цвета или фонового изображения",
      "Перемещение элементов по рабочей области",
      "Настройка размеров таймера, счёта и логотипов",
      "Изменение расположения периода и удалений",
      "Сохранение готовых вариантов оформления",
      "Использование пресетов для разных площадок",
    ],
    seoText:
      "Программа позволяет сформировать электронное хоккейное табло и передать изображение на большой экран через HDMI, DisplayPort или другое доступное видеоподключение.",
  },
  {
    id: "teams",
    eyebrow: "Подготовка матча",
    title: "Создание хоккейных команд и составов игроков",
    icon: GroupsRoundedIcon,
    image: "/features/teams.png",
    alt: "Создание команд и составов игроков в СпортПодсчет Хоккей",
    reverse: true,
    accent: "#F28C00",
    paragraphs: [
      "Команды и составы сохраняются в системе и могут использоваться повторно. Перед матчем оператор выбирает готовые команды или создаёт новые.",
      "Для каждого хоккеиста можно указать номер, имя или фамилию и добавить фотографию. Для команды можно загрузить логотип, который будет отображаться в панели управления и на зрительском табло.",
    ],
    features: [
      "Название и логотип команды",
      "Номера игроков",
      "Имена и фамилии хоккеистов",
      "Фотографии игроков",
      "Добавление игроков вручную",
      "Изменение состава перед матчем",
      "Сохранение постоянных составов",
      "Повторное использование команд в новых матчах",
    ],
    seoText:
      "Раздел команд помогает подготовить состав хоккейной команды к матчу и сохранить данные игроков для последующих игр и турниров.",
  },
  {
    id: "remote-control",
    eyebrow: "Мобильное управление",
    title: "Управление хоккейным матчем с телефона или планшета",
    icon: PhoneIphoneRoundedIcon,
    image: "/features/remote.png",
    alt: "Управление хоккейным матчем с телефона через СпортПодсчет Хоккей",
    reverse: false,
    accent: "#00A7D8",
    paragraphs: [
      "В онлайн-версии панель управления можно открыть в браузере на смартфоне, планшете или другом компьютере. Оператор получает доступ к основным функциям матча без установки отдельного приложения на мобильное устройство.",
      "Зрительское табло при этом остаётся открытым на основном экране площадки и обновляется по мере действий оператора.",
    ],
    features: [
      "Запуск и остановка игрового времени",
      "Изменение счёта",
      "Переключение периода",
      "Добавление удалений",
      "Вызов командного тайм-аута",
      "Работа со смартфона или планшета",
      "Доступ с другого компьютера",
      "Синхронное обновление зрительского табло",
    ],
    notice:
      "Для удалённого управления через онлайн-версию требуется стабильное интернет-соединение.",
    seoText:
      "Функция подходит для управления хоккейным табло с телефона, планшета или удалённого компьютера через обычный веб-браузер.",
  },
  {
    id: "media-center",
    eyebrow: "Звук во время матча",
    title: "Медиацентр и музыкальное сопровождение хоккейного матча",
    icon: MusicNoteRoundedIcon,
    image: "/features/integrations.png",
    alt: "Медиацентр и музыкальное сопровождение хоккейного матча",
    reverse: true,
    accent: "#8C5DE8",
    paragraphs: [
      "Встроенный медиацентр помогает оператору управлять музыкой и звуковыми эффектами, не переключаясь между панелью матча и сторонним проигрывателем.",
      "Музыкальные файлы можно объединять в библиотеку и назначать определённым игровым ситуациям. Оператор также может запускать нужный трек вручную.",
    ],
    features: [
      "Добавление музыкальных и звуковых файлов",
      "Создание библиотеки треков",
      "Ручной запуск и остановка воспроизведения",
      "Музыка после гола",
      "Сопровождение пауз и перерывов",
      "Назначение тегов и сценариев трекам",
      "Плавное изменение громкости",
      "Управление медиа из интерфейса матча",
    ],
    seoText:
      "Медиацентр СпортПодсчет Хоккей предназначен для музыкального оформления хоккейных матчей, тренировок и турниров на ледовой арене.",
  },
  {
    id: "matches-history",
    eyebrow: "Хранение данных",
    title: "История хоккейных матчей и турнирный модуль",
    icon: HistoryRoundedIcon,
    image: "/features/history.png",
    alt: "История хоккейных матчей и турниров в СпортПодсчет Хоккей",
    reverse: false,
    accent: "#E85D75",
    paragraphs: [
      "Созданные матчи сохраняются в системе. Оператор может вернуться к ранее созданной игре, проверить команды, открыть панель управления или использовать сохранённые данные при подготовке следующего матча.",
      "Матчи можно связывать с турнирами, формировать расписание и работать с результатами соревнований. Возможности турнирного раздела продолжают расширяться.",
    ],
    features: [
      "Список созданных и проведённых матчей",
      "Дата, команды и текущий статус игры",
      "Повторное открытие сохранённого матча",
      "Редактирование параметров игры",
      "Завершение и хранение результата",
      "Создание хоккейных турниров",
      "Привязка матчей к турниру",
      "Расписание и результаты соревнований",
    ],
    notice:
      "Объём возможностей турнирного модуля может отличаться в зависимости от используемой версии системы.",
    seoText:
      "Раздел предназначен для хранения истории хоккейных матчей, подготовки расписания и организации турниров.",
  },
];

const softwareStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "СпортПодсчет Хоккей",
  applicationCategory: "SportsApplication",
  operatingSystem: "Windows, Web browser",
  url: "https://sportpodschet.partner-progress.ru/features",
  description:
    "Программа для хоккейного хронометража, управления счётом и удалениями, создания составов, вывода табло и управления матчем с телефона.",
  featureList: [
    "Хоккейный таймер",
    "Управление счётом",
    "Периоды и перерывы",
    "Удаления и штрафное время",
    "Тайм-ауты",
    "Овертайм и буллиты",
    "Команды и составы",
    "Зрительское хоккейное табло",
    "Управление с телефона",
    "Музыкальное сопровождение матча",
    "История матчей",
    "Хоккейные турниры",
  ],
};

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Главная",
      item: "https://sportpodschet.partner-progress.ru/",
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Возможности СпортПодсчет Хоккей",
      item: "https://sportpodschet.partner-progress.ru/features",
    },
  ],
};

function FeatureSection({ item, index }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const Icon = item.icon;

  const textOrder = item.reverse
    ? {
        xs: 1,
        md: 2,
      }
    : {
        xs: 1,
        md: 1,
      };

  const imageOrder = item.reverse
    ? {
        xs: 2,
        md: 1,
      }
    : {
        xs: 2,
        md: 2,
      };

  return (
    <Box
      component="section"
      id={item.id}
      sx={{
        py: {
          xs: 7,
          md: 11,
        },
        scrollMarginTop: 88,
        bgcolor:
          index % 2 === 0
            ? "background.default"
            : isDark
              ? alpha(theme.palette.common.white, 0.025)
              : "#F6F8FC",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: 1320,
        }}
      >
        <Grid
          container
          spacing={{
            xs: 5,
            md: 8,
            lg: 10,
          }}
          alignItems="center"
        >
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
            order={textOrder}
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
              sx={{
                mb: 2,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: 2.4,
                  color: item.accent,
                  bgcolor: alpha(item.accent, isDark ? 0.16 : 0.09),
                }}
              >
                <Icon />
              </Box>

              <Typography
                component="p"
                sx={{
                  color: item.accent,
                  fontSize: 13,
                  lineHeight: 1.4,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                {item.eyebrow}
              </Typography>
            </Stack>

            <Typography
              component="h2"
              sx={{
                maxWidth: 680,
                fontSize: {
                  xs: "2rem",
                  md: "2.75rem",
                },
                lineHeight: 1.12,
                fontWeight: 850,
                letterSpacing: "-0.04em",
              }}
            >
              {item.title}
            </Typography>

            <Stack
              spacing={1.6}
              sx={{
                mt: 2.5,
              }}
            >
              {item.paragraphs.map((paragraph) => (
                <Typography
                  key={paragraph}
                  component="p"
                  color="text.secondary"
                  sx={{
                    maxWidth: 720,
                    fontSize: {
                      xs: 16,
                      md: 17,
                    },
                    lineHeight: 1.75,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Stack>

            <Grid
              container
              spacing={1.25}
              sx={{
                mt: 3,
              }}
            >
              {item.features.map((feature) => (
                <Grid
                  key={feature}
                  size={{
                    xs: 12,
                    sm: 6,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <CheckCircleRoundedIcon
                      sx={{
                        mt: 0.25,
                        flex: "0 0 auto",
                        color: item.accent,
                        fontSize: 20,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: 14.5,
                        lineHeight: 1.55,
                        fontWeight: 650,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            {item.secondaryTitle && (
              <Paper
                elevation={0}
                sx={{
                  mt: 3.5,
                  p: {
                    xs: 2.5,
                    md: 3,
                  },
                  borderRadius: 3.5,
                  border: `1px solid ${alpha(
                    item.accent,
                    isDark ? 0.28 : 0.18
                  )}`,
                  bgcolor: alpha(
                    item.accent,
                    isDark ? 0.09 : 0.045
                  ),
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                >
                  <TuneRoundedIcon
                    sx={{
                      color: item.accent,
                    }}
                  />

                  <Typography
                    component="h3"
                    sx={{
                      fontSize: 18,
                      fontWeight: 850,
                    }}
                  >
                    {item.secondaryTitle}
                  </Typography>
                </Stack>

                <Grid
                  container
                  spacing={1.1}
                  sx={{
                    mt: 1.7,
                  }}
                >
                  {item.secondaryFeatures.map((feature) => (
                    <Grid
                      key={feature}
                      size={{
                        xs: 12,
                        sm: 6,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.9}
                        alignItems="flex-start"
                      >
                        <CheckCircleRoundedIcon
                          sx={{
                            mt: 0.25,
                            flex: "0 0 auto",
                            color: item.accent,
                            fontSize: 18,
                          }}
                        />

                        <Typography
                          color="text.secondary"
                          sx={{
                            fontSize: 14,
                            lineHeight: 1.55,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {item.notice && (
              <Box
                sx={{
                  mt: 3,
                  px: 2.4,
                  py: 1.8,
                  borderRadius: 3,
                  border: `1px solid ${alpha(
                    item.accent,
                    isDark ? 0.25 : 0.16
                  )}`,
                  bgcolor: alpha(
                    item.accent,
                    isDark ? 0.08 : 0.04
                  ),
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.1}
                  alignItems="flex-start"
                >
                  <CloudOutlinedIcon
                    sx={{
                      mt: 0.1,
                      flex: "0 0 auto",
                      color: item.accent,
                      fontSize: 21,
                    }}
                  />

                  <Typography
                    color="text.secondary"
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.6,
                    }}
                  >
                    {item.notice}
                  </Typography>
                </Stack>
              </Box>
            )}

            <Typography
              component="p"
              color="text.secondary"
              sx={{
                mt: 3,
                fontSize: 14,
                lineHeight: 1.65,
              }}
            >
              {item.seoText}
            </Typography>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
            order={imageOrder}
          >
            <Paper
              elevation={0}
              sx={{
                position: "relative",
                overflow: "hidden",
                minHeight: {
                  xs: 300,
                  sm: 390,
                  md: 470,
                },
                borderRadius: {
                  xs: 3.5,
                  md: 5,
                },
                border: `1px solid ${alpha(
                  item.accent,
                  isDark ? 0.24 : 0.14
                )}`,
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.04)
                  : "#FFFFFF",
                boxShadow: isDark
                  ? "0 26px 70px rgba(0,0,0,0.24)"
                  : "0 28px 75px rgba(40,75,125,0.12)",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: {
                    xs: 10,
                    md: 14,
                  },
                  overflow: "hidden",
                  borderRadius: {
                    xs: 2.7,
                    md: 3.8,
                  },
                  bgcolor: isDark ? "#101827" : "#F4F7FB",
                }}
              >
                <Image
                  src={item.image}
                  alt={item.alt}
                  fill
                  sizes="(max-width: 900px) 100vw, 50vw"
                  priority={index === 0}
                  style={{
                    objectFit: "contain",
                    objectPosition: "center",
                  }}
                />
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  width: 220,
                  height: 220,
                  right: -100,
                  top: -100,
                  borderRadius: "50%",
                  bgcolor: alpha(
                    item.accent,
                    isDark ? 0.15 : 0.08
                  ),
                  pointerEvents: "none",
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function ActionSection({
  eyebrow,
  title,
  description,
  primaryText,
  primaryIcon,
  onPrimaryClick,
  secondaryText,
  secondaryIcon,
  onSecondaryClick,
  dark = false,
}) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        py: {
          xs: 7,
          md: 10,
        },
        bgcolor: dark
          ? "transparent"
          : theme.palette.background.default,
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          maxWidth: 1320,
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: {
              xs: 3,
              sm: 5,
              md: 8,
            },
            py: {
              xs: 5,
              md: 7,
            },
            borderRadius: {
              xs: 4,
              md: 6,
            },
            color: "#FFFFFF",
            background:
              "linear-gradient(135deg, #005BDA 0%, #0073FF 52%, #00A7D8 100%)",
            boxShadow:
              "0 30px 80px rgba(0,115,255,0.22)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 360,
              height: 360,
              right: -110,
              top: -170,
              borderRadius: "50%",
              bgcolor: "rgba(255,255,255,0.10)",
            }}
          />

          <Grid
            container
            spacing={{
              xs: 4,
              md: 6,
            }}
            alignItems="center"
            sx={{
              position: "relative",
            }}
          >
            <Grid
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <Typography
                component="p"
                sx={{
                  mb: 1.4,
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.78)",
                }}
              >
                {eyebrow}
              </Typography>

              <Typography
                component="h2"
                sx={{
                  maxWidth: 850,
                  fontSize: {
                    xs: "2rem",
                    md: "3rem",
                  },
                  lineHeight: 1.1,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  maxWidth: 800,
                  mt: 2,
                  fontSize: {
                    xs: 16,
                    md: 18,
                  },
                  lineHeight: 1.7,
                  color: "rgba(255,255,255,0.84)",
                }}
              >
                {description}
              </Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Stack
                spacing={1.5}
                alignItems={{
                  xs: "stretch",
                  md: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={primaryIcon}
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={onPrimaryClick}
                  sx={{
                    minHeight: 54,
                    px: 3.3,
                    borderRadius: 2.5,
                    bgcolor: "#FFFFFF",
                    color: "#005BDA",
                    textTransform: "none",
                    fontSize: 15.5,
                    fontWeight: 850,
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.91)",
                    },
                  }}
                >
                  {primaryText}
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  startIcon={secondaryIcon}
                  onClick={onSecondaryClick}
                  sx={{
                    minHeight: 52,
                    px: 3.2,
                    borderRadius: 2.5,
                    borderColor: "rgba(255,255,255,0.55)",
                    color: "#FFFFFF",
                    textTransform: "none",
                    fontSize: 15,
                    fontWeight: 800,
                    "&:hover": {
                      borderColor: "#FFFFFF",
                      bgcolor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  {secondaryText}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}

export default function FeaturesPage() {
  const router = useRouter();
  const theme = useTheme();

  const isDark = theme.palette.mode === "dark";

  const openDemo = () => {
    router.push("/demo");
  };

  const openOnline = () => {
    router.push("/login");
  };

  const openContacts = () => {
    router.push("/contacts");
  };

  return (
    <>
      <Head>
        <title>
          Возможности СпортПодсчет Хоккей — таймер, табло, удаления и составы
        </title>

        <meta
          name="description"
          content="Подробные возможности СпортПодсчет Хоккей: игровой таймер, счёт, периоды, удаления, составы команд, настраиваемое хоккейное табло, управление с телефона и музыкальное сопровождение."
        />

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <link
          rel="canonical"
          href="https://sportpodschet.partner-progress.ru/features"
        />

        <meta
          property="og:title"
          content="Возможности программы СпортПодсчет Хоккей"
        />

        <meta
          property="og:description"
          content="Хоккейный хронометраж, табло, удаления, составы команд, управление с телефона и медиацентр."
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:locale"
          content="ru_RU"
        />

        <meta
          property="og:url"
          content="https://sportpodschet.partner-progress.ru/features"
        />

        <meta
          property="og:image"
          content="https://sportpodschet.partner-progress.ru/screens/control_panel.webp"
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(softwareStructuredData),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbStructuredData),
          }}
        />
      </Head>

      <Box
        component="main"
        sx={{
          overflow: "hidden",
        }}
      >
        {/* Заголовок страницы */}

        <Box
          component="header"
          sx={{
            position: "relative",
            overflow: "hidden",
            pt: {
              xs: 8,
              md: 11,
            },
            pb: {
              xs: 7,
              md: 9,
            },
            background: isDark
              ? `
                  linear-gradient(
                    135deg,
                    rgba(8,19,36,0.98) 0%,
                    rgba(8,35,65,0.94) 52%,
                    rgba(5,58,85,0.88) 100%
                  )
                `
              : `
                  linear-gradient(
                    135deg,
                    rgba(248,251,255,0.98) 0%,
                    rgba(232,243,255,0.94) 52%,
                    rgba(221,242,248,0.90) 100%
                  )
                `,
            borderBottom: `1px solid ${alpha(
              theme.palette.text.primary,
              0.06
            )}`,
            "&::before": {
              content: '""',
              position: "absolute",
              width: 650,
              height: 650,
              top: -380,
              right: -180,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(0,167,216,0.20) 0%, rgba(0,115,255,0.07) 48%, transparent 72%)",
            },
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: 1200,
              textAlign: "center",
            }}
          >
            <Typography
              component="p"
              sx={{
                color: "primary.main",
                fontSize: 13,
                fontWeight: 850,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              Возможности программы
            </Typography>

            <Typography
              component="h1"
              sx={{
                maxWidth: 900,
                mx: "auto",
                mt: 1.8,
                fontSize: {
                  xs: "2.45rem",
                  sm: "3.2rem",
                  md: "4rem",
                },
                lineHeight: 1.05,
                fontWeight: 850,
                letterSpacing: "-0.05em",
              }}
            >
              Возможности СпортПодсчет Хоккей
            </Typography>

            <Typography
              component="p"
              color="text.secondary"
              sx={{
                maxWidth: 900,
                mx: "auto",
                mt: 2.5,
                fontSize: {
                  xs: 16.5,
                  md: 19,
                },
                lineHeight: 1.7,
              }}
            >
              Подробно о хоккейном хронометраже, управлении счётом
              и удалениями, составах команд, зрительском табло,
              музыкальном сопровождении и удалённом управлении.
            </Typography>

            <Stack
              direction="row"
              useFlexGap
              flexWrap="wrap"
              justifyContent="center"
              gap={1}
              sx={{
                mt: 4,
              }}
            >
              {sectionNavigation.map((item) => (
                <Chip
                  key={item.id}
                  component="a"
                  href={`#${item.id}`}
                  clickable
                  label={item.label}
                  sx={{
                    height: 38,
                    px: 0.5,
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      isDark ? 0.3 : 0.18
                    )}`,
                    bgcolor: isDark
                      ? alpha(
                          theme.palette.primary.main,
                          0.12
                        )
                      : "rgba(255,255,255,0.58)",
                    color: "text.primary",
                    fontWeight: 750,
                    backdropFilter: "blur(10px)",
                    textDecoration: "none",
                    "&:hover": {
                      bgcolor: alpha(
                        theme.palette.primary.main,
                        isDark ? 0.19 : 0.1
                      ),
                    },
                  }}
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Подробные возможности */}

        {featureSections.slice(0, 4).map((item, index) => (
          <FeatureSection
            key={item.id}
            item={item}
            index={index}
          />
        ))}

        {/* Первый призыв к действию */}

        <ActionSection
          eyebrow="Демонстрация"
          title="Посмотрите полный сценарий проведения хоккейного матча"
          description="Покажем создание команд, запуск игрового времени, управление счётом и удалениями, а также вывод зрительского табло на отдельный экран."
          primaryText="Посмотреть демонстрацию"
          primaryIcon={<PlayCircleOutlineRoundedIcon />}
          onPrimaryClick={openDemo}
          secondaryText="Открыть онлайн-версию"
          secondaryIcon={<LanguageRoundedIcon />}
          onSecondaryClick={openOnline}
        />

        {featureSections.slice(4).map((item, index) => (
          <FeatureSection
            key={item.id}
            item={item}
            index={index + 4}
          />
        ))}

        {/* Краткий блок дополнительных функций */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 7,
              md: 10,
            },
            bgcolor: isDark
              ? alpha(theme.palette.common.white, 0.025)
              : "#F6F8FC",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              maxWidth: 1320,
            }}
          >
            <Box
              sx={{
                maxWidth: 820,
                mx: "auto",
                mb: {
                  xs: 4,
                  md: 6,
                },
                textAlign: "center",
              }}
            >
              <Typography
                component="p"
                sx={{
                  color: "primary.main",
                  fontSize: 13,
                  fontWeight: 850,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                }}
              >
                Дополнительные инструменты
              </Typography>

              <Typography
                component="h2"
                sx={{
                  mt: 1.5,
                  fontSize: {
                    xs: "2rem",
                    md: "2.8rem",
                  },
                  lineHeight: 1.12,
                  fontWeight: 850,
                  letterSpacing: "-0.04em",
                }}
              >
                Всё необходимое оператору во время матча
              </Typography>
            </Box>

            <Grid
              container
              spacing={2.5}
            >
              {[
                {
                  icon: TvRoundedIcon,
                  title: "Несколько экранов",
                  description:
                    "Панель оператора и зрительское табло могут быть открыты на разных мониторах.",
                },
                {
                  icon: TuneRoundedIcon,
                  title: "Настройки под площадку",
                  description:
                    "Параметры времени, периодов, перерывов, тайм-аутов и оформления сохраняются в системе.",
                },
                {
                  icon: EmojiEventsRoundedIcon,
                  title: "Матчи и турниры",
                  description:
                    "Созданные игры можно хранить, повторно открывать и объединять в соревнования.",
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <Grid
                    key={item.title}
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        height: "100%",
                        p: {
                          xs: 3,
                          md: 3.5,
                        },
                        borderRadius: 4,
                        border: `1px solid ${alpha(
                          theme.palette.text.primary,
                          0.08
                        )}`,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 2.7,
                          color: "primary.main",
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            isDark ? 0.15 : 0.08
                          ),
                        }}
                      >
                        <Icon />
                      </Box>

                      <Typography
                        component="h3"
                        sx={{
                          mt: 2.3,
                          fontSize: 20,
                          fontWeight: 850,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        color="text.secondary"
                        sx={{
                          mt: 1,
                          lineHeight: 1.7,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        {/* Финальный призыв */}

        <ActionSection
          eyebrow="Подбор решения"
          title="Подберите возможности СпортПодсчет Хоккей для своей площадки"
          description="Покажем работу нужных функций, поможем выбрать подходящий формат и расскажем, как подключить компьютер, телефон и зрительское табло."
          primaryText="Обсудить конфигурацию"
          primaryIcon={<ScoreboardRoundedIcon />}
          onPrimaryClick={openContacts}
          secondaryText="Посмотреть демонстрацию"
          secondaryIcon={<PlayCircleOutlineRoundedIcon />}
          onSecondaryClick={openDemo}
          dark
        />
      </Box>
    </>
  );
}

FeaturesPage.getLayout = (page) => (
  <LandingLayout>{page}</LandingLayout>
);